/**
 * Sendungen (Transportsendungen, DSV). Anders als `bestellungen.ts`/`lieferanten.ts` KEIN
 * `ressource()`-Zugriff: `GET /api/dsv/shipments` (`backend/src/http/routes/dsvShipments.ts:333`)
 * ist eine SUCHE (`?q=`), kein "liste alles"-Endpunkt - `POST` dort
 * nimmt zudem ein eigenes `nummer`-Feld statt eines generischen Datensatzes entgegen. Ein
 * `ressource<Sendung>(...)` wuerde `anlegen`/`aendern`/`loeschen` vortaeuschen, die es an dieser
 * Route so nicht gibt.
 *
 * ENTFALLEN (Auftrag „Sendungsabruf beim App-Start entfernen", 2026-08-22): diese Datei fuehrte hier
 * eine weitere Funktion, die `GET /api/dsv/shipments` bewusst OHNE `q` aufrief, um beim App-Start
 * alle Sendungen auf einen Schlag zu holen (`state/Speicher.tsx#bestandLaden`, Zweig 2). Zwei
 * Befunde haben das beendet, nicht nur einer:
 *
 *   1. Der Sammelabruf war BIS ZUM 22.08.2026 garantiert leer: eine leere Suche blieb unter
 *      `SUCHE_MINDESTLAENGE`, die Route lieferte dafuer immer `[]`, unabhaengig vom tatsaechlichen
 *      Bestand an `shipments`. SEIT `fc0fc26` (22.08.2026) liefert genau derselbe Aufruf bei leerem
 *      `q` bis zu 50 echte, zuletzt bewegte Sendungen. Das rettet den Sammelabruf NICHT, sondern
 *      macht ihn erst spuerbar teuer: Ladezeit bei JEDEM App-Start fuer bis zu 50 Saetze, die kein
 *      Verbraucher liest. Das leere Array war die ganze Zeit ueber die Tarnung fuer Befund 2 unten,
 *      nicht der Grund fuer das Entfernen.
 *   2. *FORMBRUCH* (der eigentliche Grund, von `fc0fc26` unberuehrt): selbst mit Nutzlast waere die
 *      Antwortform falsch gewesen - die entfernte Funktion war als `Promise<Sendung[]>` deklariert,
 *      die Route liefert aber `SendungsTreffer[]` (siehe Abschnittsgrenze unten). Die beiden Formen
 *      sind unvereinbar: `Sendung` fuehrt `trackingNo`/`stationen`/`zoll`/`dischargePort`,
 *      `SendungsTreffer` fuehrt `dsvShipmentNo`/`verknuepfteBestellungen`/`portalUrl` - und KEINES
 *      der vier. Aufgefallen ist das nie, weil `Sendung` (`../lib/types.ts`) eine offene
 *      Index-Signatur traegt, die jeden Feldzugriff klaglos durchwinkt - mit echten Sendungen im
 *      Array haette dieser Formbruch also weiterhin nicht als Typfehler angeschlagen, sondern nur
 *      als still falsche Werte.
 *
 * Ein gesetztes `order.shipmentId` haette den Formbruch zusaetzlich gefaehrlich gemacht: die damalige
 * Aufloesungsfunktion in `lib/suche.ts` (mit diesem Auftrag ebenfalls entfernt) haette dann bis zu
 * 50 falsch geformte Sendungen auf einen Schlag gegen die Board-Sperre aktiviert - ein weiterer
 * Beleg dafuer, den Pfad ganz zu entfernen statt ihn zu reparieren.
 *
 * Die Aufloesung je Bestellung lief seither ausschliesslich ueber die Huelle weiter unten in diesem
 * Modul (Zugriff auf `GET /api/orders/:orderId/shipment`), gezielt fuer GENAU EINE Bestellung, in
 * der Form, die die Route tatsaechlich liefert (`SendungsTreffer | null`).
 * Ausfuehrlich, samt der beiden erwogenen Wege und was dabei bewusst offen bleibt:
 * `docs/superpowers/decisions/2026-08-22-sendungen-app-start.md`.
 *
 * FORTSETZUNG (Auftrag „Sammelroute fuer die Zeitstrahl-Liste", 25.08.2026): der oben entfernte
 * Sammelabruf war aus zwei Gruenden unbrauchbar - `GET /api/dsv/shipments` ohne `q` kennt keine
 * Bestellungen (es ist eine SENDUNGSSUCHE) und liefert die falsche Form (`SendungsTreffer[]` statt
 * des dort erwarteten `Sendung[]`). `sendungenZuBestellungenLaden()` unten ist KEIN Nachbau dieser
 * Funktion, sondern eine ANDERE Route: `POST /api/orders/shipments` (neu,
 * `backend/src/http/routes/orderShipments.ts`, direkt neben der Einzelroute) nimmt eine explizite
 * Liste von `orderIds` entgegen und liefert je Bestellung deren verknuepfte Sendung in GENAU DER
 * FORM der Einzelroute (`SendungsTreffer | null`, ueber dasselbe `alsTreffer()`) - kein Formbruch,
 * weil hier (anders als beim entfernten Sammelabruf) niemand `Sendung` erwartet: der Aufrufer
 * (`ZeitstrahlSeite.tsx` ueber `App.tsx`) bildet die zurueckkommenden `SendungsTreffer` selbst auf
 * die dortige `Sendung`-Fixtureform ab, wo noetig. Und: geladen wird gezielt fuer die aktuell
 * sichtbaren Bestellungen der Zeitstrahl-Liste, nicht mehr beim App-Start (siehe `App.tsx`).
 *
 * ================= Reiter "Spedition" (Drawer) - die vollstaendige Nahtstelle =================
 *
 * Ab hier die Funktionen fuer den eigenen Drawer-Reiter, in dem eine DSV-Sendung gesucht,
 * verknuepft und geloest wird sowie der daraus abgeleitete Zeitstrahl geladen wird - seit Auftrag
 * 2b (25.08.2026) dazu `sendungsVorschlagLaden()`, seit dem Auftrag "DSV-Belege ..." (04.09.2026)
 * dazu `dsvBelegeLaden()`, und bis auf `sendungenZuBestellungenLaden` (siehe "FORTSETZUNG" oben), die
 * stattdessen die Zeitstrahl-LISTE speist. Alle neun Funktionen dieses Moduls laufen ueber
 * `anfrage()` aus `./basis.ts` - dem einzigen Ort, an dem das Frontend `fetch` gegen die eigene API
 * ruft (siehe dessen Kopfkommentar).
 *
 * `SendungsTreffer` (`../lib/types.ts`) ist die SERVERFORM - wortgleich zum gleichnamigen Interface
 * in `backend/src/http/routes/dsvShipments.ts`. Sie ist eine ANDERE Form als `Sendung`
 * (`../lib/types.ts`, die Fixtureform aus dem Task-10-Brief, mit `etd`/`stationen`/`zoll`, die diese
 * Routen nicht liefern) - `Sendung` bleibt unveraendert und unbenannt, `ZeitstrahlSeite.tsx` und
 * `lib/suche.ts` haengen weiterhin an ihr, gespeist aus der je Bestellung aufgeloesten Sendung.
 *
 * Suchbegriffe gehen durch `encodeURIComponent`, weil sie aus einem Eingabefeld kommen und `&`,
 * `#`, `%` enthalten duerfen. Eine Mindestlaenge gibt es seit dem 22.08.2026 nicht mehr (siehe
 * oben): jeder Begriff sucht, ein leerer liefert die zuletzt bewegten Sendungen. Diese Datei
 * drosselt nicht selbst - das bleibt Sache des Bedienelements (`api/jtlSuche.ts`).
 */
import { anfrage } from "./basis.ts";
import type {
  DsvBeleg,
  DsvEreignis,
  SammelsendungJtlErgebnis,
  SendungsTreffer,
  SendungsVorschlag,
  Zeitstrahl,
} from "../lib/types.ts";

/**
 * Die Sendungssuche (`GET /api/dsv/shipments?q=<begriff>`,
 * `backend/src/http/routes/dsvShipments.ts#sendungenSuchen`). Der Begriff wird kodiert - siehe
 * Kopfkommentar zur Kodierung und zum entfallenen Mindestlaengen-Riegel.
 */
export function sendungenSuchen(begriff: string, signal?: AbortSignal): Promise<SendungsTreffer[]> {
  return anfrage<SendungsTreffer[]>(`/api/dsv/shipments?q=${encodeURIComponent(begriff)}`, {
    signal,
  });
}

/** Eine einzelne Sendung (`GET /api/dsv/shipments/:id`). */
export function sendungLaden(id: string, signal?: AbortSignal): Promise<SendungsTreffer> {
  return anfrage<SendungsTreffer>(`/api/dsv/shipments/${encodeURIComponent(id)}`, { signal });
}

/**
 * Die mit einer Bestellung verknuepfte Sendung, oder `null` (`GET /api/orders/:orderId/shipment`,
 * `backend/src/http/routes/orderShipments.ts`). Die Route antwortet mit `{ sendung: SendungsTreffer
 * | null }` - diese Funktion wickelt die Huelle ab, ihr Aufrufer sieht nur noch den Treffer selbst
 * oder `null`.
 */
export function sendungZuBestellungLaden(
  orderId: string,
  signal?: AbortSignal,
): Promise<SendungsTreffer | null> {
  return anfrage<{ sendung: SendungsTreffer | null }>(
    `/api/orders/${encodeURIComponent(orderId)}/shipment`,
    { signal },
  ).then((antwort) => antwort.sendung);
}

/**
 * Der Zuordnungsvorschlag einer (noch unverknuepften) Bestellung, oder `null`
 * (`GET /api/orders/:orderId/shipment/vorschlag`, Auftrag 2b, Zuordnungsvorschlag mit
 * "Uebernehmen", 25.08.2026). Die Route wickelt dieselbe `{ vorschlag: ... | null }`-Huelle ab wie
 * `sendungZuBestellungLaden` oben.
 */
export function sendungsVorschlagLaden(
  orderId: string,
  signal?: AbortSignal,
): Promise<SendungsVorschlag | null> {
  return anfrage<{ vorschlag: SendungsVorschlag | null }>(
    `/api/orders/${encodeURIComponent(orderId)}/shipment/vorschlag`,
    { signal },
  ).then((antwort) => antwort.vorschlag);
}

/**
 * Die mit MEHREREN Bestellungen verknuepften Sendungen auf einen Schlag (`POST
 * /api/orders/shipments`, `backend/src/http/routes/orderShipments.ts`) - Schluessel ist die
 * `orderId`, wortgleich zu den `orderIds` des Aufrufs. Bestellungen ohne Verknuepfung stehen als
 * `null` IM Record, nicht als fehlender Schluessel (siehe Kopfkommentar, "FORTSETZUNG") - der
 * Aufrufer unterscheidet damit "nicht verknuepft" von "noch nicht geladen" (fehlt im Record).
 * `POST` statt `GET` (wie bei `sendungVerknuepfen`), weil die Liste bei einigen hundert
 * Bestellungen eine Query-URL sprengen wuerde.
 */
export function sendungenZuBestellungenLaden(
  orderIds: readonly string[],
  signal?: AbortSignal,
  // Nur der automatische Nachlader der Zeitstrahl-Liste setzt das (`useZeitstrahlSendungen`, wenn bei
  // gleicher Seite nur der Bestand wechselt): dann geht die Anfrage als Hintergrundanfrage hinaus
  // (`AnfrageOptionen.hintergrund`).
  hintergrund?: boolean,
): Promise<Record<string, SendungsTreffer | null>> {
  return anfrage<{ sendungen: Record<string, SendungsTreffer | null> }>("/api/orders/shipments", {
    methode: "POST",
    koerper: { orderIds },
    signal,
    hintergrund,
  }).then((antwort) => antwort.sendungen);
}

/**
 * Verknuepft eine Bestellung mit einer Sendung (`POST /api/orders/:orderId/shipment`,
 * `backend/src/http/routes/orderShipments.ts`). Liefert die vom Server ermittelten Abweichungen
 * sowie die neue `rowVersion` der Bestellung.
 *
 * `umhaengen` (Sammelsendung, Spec 2026-09-07): ersetzt das fruehere `bestehendeLoesen` - GEGENTEILIGE
 * Bedeutung, keine abgeschwaechte (siehe Kopfkommentar `backend/src/http/routes/orderShipments.ts`).
 * `bestehendeLoesen` trennte FREMDE Bestellungen von der Zielsendung; `umhaengen` loest stattdessen
 * die BISHERIGE Verknuepfung der ANFRAGENDEN Bestellung zu ihrer alten Sendung, weil eine Bestellung
 * seit Migration 039 an hoechstens einer Sendung haengen darf. Ohne `umhaengen` antwortet die Route
 * `409` mit `{ error, bisherigeSendung }`, den `anfrage()` als `KonfliktFehler` wirft (`api/basis.ts`)
 * - der Aufrufer (`App.tsx#speditionVerknuepfen`) fragt dort nach und ruft bei Zustimmung erneut mit
 * `umhaengen: true` auf. NUR bei `true` im Koerper aufgenommen: ein `undefined`- oder `false`-Feld im
 * Koerper wuerde `test/komponenten/api/sendungen.test.ts` ("sendet POST mit { shipmentId } im
 * Koerper") und die gleichlautende Zusicherung in `verdrahtungDrawerReiter.test.tsx` brechen, die
 * exakt ZWEI Argumente gegen `sendungVerknuepfen` pruefen - "ohne das Feld verhaelt sich die Route
 * exakt wie heute" gilt spiegelbildlich auch fuer diese Huelle.
 */
/**
 * Antwort des Verknuepfens. `jtlErgebnisse` steht nur, wenn die Bestellung einer Sammelsendung mit zentraler
 * Rechnungsnummer beigetreten ist: je Kind, ob die Nummer nach JTL geschrieben wurde (der Beitritt selbst
 * bleibt auch bei einer Ablehnung bestehen).
 */
export type VerknuepfungsAntwort = {
  abweichungen: unknown[];
  orderRowVersion: number;
  jtlErgebnisse?: SammelsendungJtlErgebnis[];
};

export function sendungVerknuepfen(
  orderId: string,
  shipmentId: string,
  umhaengen?: boolean,
): Promise<VerknuepfungsAntwort> {
  const koerper: Record<string, unknown> = umhaengen ? { shipmentId, umhaengen: true } : { shipmentId };
  return anfrage<VerknuepfungsAntwort>(
    `/api/orders/${encodeURIComponent(orderId)}/shipment`,
    { methode: "POST", koerper },
  );
}

/** Loest die Verknuepfung wieder (`DELETE /api/orders/:orderId/shipment/:shipmentId`). */
export async function verknuepfungLoesen(orderId: string, shipmentId: string): Promise<void> {
  await anfrage<{ ok: true }>(
    `/api/orders/${encodeURIComponent(orderId)}/shipment/${encodeURIComponent(shipmentId)}`,
    { methode: "DELETE" },
  );
}

/**
 * Der Zeitstrahl einer Bestellung (`GET /api/orders/:orderId/zeitstrahl`,
 * `backend/src/http/routes/orderShipments.ts`) - Quelle `"dsv"` (echte Stationsereignisse einer
 * verknuepften Sendung, `dsv/zeitstrahl.ts#zeitstrahlAusEreignissen`) oder `"board"` (keine Sendung
 * verknuepft, sechs Stufen ohne Zeitpunkte, `aktuelleStufeIndex: -1`).
 */
export function zeitstrahlLaden(orderId: string, signal?: AbortSignal): Promise<Zeitstrahl> {
  return anfrage<Zeitstrahl>(`/api/orders/${encodeURIComponent(orderId)}/zeitstrahl`, { signal });
}

/**
 * Die DSV-Historie einer Bestellung (`GET /api/orders/:orderId/shipment/ereignisse`,
 * `backend/src/http/routes/orderShipments.ts`) - ALLE rohen `shipment_events`-Zeilen der
 * verknuepften Sendung, UNGEFILTERT (Abschnitt „DSV-Historie" im Reiter „Spedition",
 * `Drawer/ShipTab/DsvHistorie.tsx`). Anders als `zeitstrahlLaden` oben KEINE Ableitung auf Stufen -
 * auch ein storniertes Ereignis und eines ohne Stufen-Zuordnung kommen hier an.
 */
export function dsvHistorieLaden(orderId: string, signal?: AbortSignal): Promise<DsvEreignis[]> {
  return anfrage<{ ereignisse: DsvEreignis[] }>(
    `/api/orders/${encodeURIComponent(orderId)}/shipment/ereignisse`,
    { signal },
  ).then((antwort) => antwort.ereignisse);
}

/**
 * Die DSV-Belege (Zustellbeleg + Rechnung) einer Bestellung (`GET /api/orders/:orderId/dsv-belege`,
 * `backend/src/http/routes/dsvBelege.ts`) - Auftrag "DSV-Belege (Zustellbeleg + Rechnung) in der
 * Bestellung anzeigen".
 *
 * KORRIGIERT (Pruefer-Befund, KRITISCH, Nachbesserung 05.09.2026): hier stand, `order_id` wuerde
 * beim Webhook-Empfang EINMALIG aufgeloest und bliebe danach unveraendert bestehen - das war VOR
 * dem Basis-Commit dieses Auftrags (`ac18c78b`, "Invoice-PDF-Download und Bestellzuordnung
 * nachziehen/loesen") richtig, ist es seither nicht mehr: `backend/src/dsv/belegVerknuepfung.ts`
 * bewegt `order_id` LIVE bei jedem Verknuepfen/Loesen einer Sendung. `App.tsx#speditionVerknuepfen`/
 * `#speditionLoesen` rufen diese Funktion deshalb, genau wie `zeitstrahlLaden`, nach JEDEM
 * Verknuepfen/Loesen erneut auf.
 */
export function dsvBelegeLaden(orderId: string, signal?: AbortSignal): Promise<DsvBeleg[]> {
  return anfrage<{ belege: DsvBeleg[] }>(`/api/orders/${encodeURIComponent(orderId)}/dsv-belege`, {
    signal,
  }).then((antwort) => antwort.belege);
}
